import React, { useState, useEffect } from "react";
import { Modal, Box, TextField, MenuItem, Button, Select, InputLabel, FormControl } from "@mui/material";

import './newSchedule.css';
import RouteModal from "./routeModal";
import { LocalizationProvider, TimeField } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import BootstrapTooltip from "./tooltip";
import MultiSchedulesModal from "./multiSchedulesModal";
import ImportExcelModal from "../utils/importExcelModal";
import ViewSchedulesModal from "./viewSchedulesModal";
import { mixedNaturalSort } from "../utils/generalFunctions";
import ConfirmModal from "./confirmModal";

const NewSchedule = ({ devices, userSchedules, userRoute, geofences, userUpdate, scheduleRegister, showAlert, setShowedLayout }) => {
  const [open, setOpen] = useState(false);
  const [multiScheduleModalOpen, setMultiScheduleModalOpen] = useState(false);
  const [multiScheduleCount, setMultiScheduleCount] = useState(2);
  const [scheduleName, setScheduleName] = useState("");
  const [vehicle, setVehicle] = useState("Ninguno");
  const [controlPointStart, setControlPointStart] = useState("");
  const [controlPointEnd, setControlPointEnd] = useState("");
  const [timeDifference, setTimeDifference] = useState({ minutes: 5, seconds: 0 });
  const [lastGeneratedTimeDifference, setLastGeneratedTimeDifference] = useState(null);
  const [numLaps, setNumLaps] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [routeOrder, setRouteOrder] = useState(userRoute.filter(geofence => geofence.state === 'activo').map(geofence => geofence.label));
  const [startTime, setStartTime] = useState(dayjs().hour(12).minute(0).second(0));
  const [customTimeDifferences, setCustomTimeDifferences] = useState(
    routeOrder.map(() => ({ ...timeDifference }))
  );

  const [openRouteModal, setOpenRouteModal] = useState(false);

  const [showHeaders, setShowHeaders] = useState(false);
  const [enabledGenerateMultBtn, setEnabledGenerateMultBtn] = useState(true);

  const [scheduleNameError, setScheduleNameError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingVehicleChange, setPendingVehicleChange] = useState(null);

  const [openImportModal, setOpenImportModal] = useState(false);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [prevSchedules, setPrevSchedules] = useState([]);

  useEffect(() => {
    setRouteOrder(userRoute.filter(geofence => geofence.state === 'activo').map(geofence => geofence.label));
    setCustomTimeDifferences(prev => {
      const newOrder = userRoute.filter(geofence => geofence.state === 'activo').map(geofence => geofence.label);
      const newDifferences = [];

      newOrder.forEach((label, index) => {
        const oldIndex = routeOrder.indexOf(label);
        // Clonamos el objeto para que no se compartan referencias
        newDifferences[index] =
          oldIndex !== -1 && prev[oldIndex] !== undefined
            ? { ...prev[oldIndex] }
            : { ...timeDifference };
      });

      return newDifferences;
    });

  }, [userRoute]);

  useEffect(() => {
    if (open === true) {
      setTableData(handleFillTable());
    }
  }, [routeOrder]);



  const clearForm = () => {
    setScheduleName("");
    setVehicle("Ninguno");
    setControlPointStart("");
    setControlPointEnd("");
    setTimeDifference({ minutes: 5, seconds: 0 });
    setNumLaps(10);
    setTableData([]);
    setStartTime(dayjs().hour(12).minute(0).second(0));
  };



  const handleOpenModal = () => {
    clearForm();
    setShowHeaders(false);
    setCustomTimeDifferences(routeOrder.map(() => ({ ...timeDifference })));
    setMultiScheduleCount(2);
    setPrevSchedules([]);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setEnabledGenerateMultBtn(true);
    setMultiScheduleCount(2);
    setShowedLayout(0);
  };

  const handleOpenImportModal = () => {
    setOpenImportModal(prev => !prev);
  };

  const handleOpenMultiScheduleModal = () => {
    if (tableData.length === 0) {
      // alert("Genere el horario correctamente antes de crear más horarios.");
      showAlert('Genere el horario correctamente antes de crear más horarios.', 'warning');
      return;
    }
    setMultiScheduleModalOpen(true);
  };

  const handleCloseMultiScheduleModal = () => setMultiScheduleModalOpen(false);

  const handleAccept = () => {
    if (scheduleNameError) {
      showAlert("Ya existe un horario con este nombre", "warning");
      return;
    }

    if (tableData.length === 0) {
      // alert("Genere el horario correctamente antes de guardarlo.");
      showAlert('Genere el horario correctamente antes de guardarlo.', 'warning');
      return;
    }

    let vehicleId = null;

    if (vehicle !== 'Ninguno') {
      vehicleId = devices.find((device) => device.id === vehicle)?.id || null;
    }

    const tableDataInSeconds = tableData.map((lap) =>
      lap.map((point) => {
        if (point.time === "-") {
          return { ...point, time: null };
        }

        const [hours, minutes, seconds] = point.time.split(":").map(Number);
        const timeInSeconds = hours * 3600 + minutes * 60 + seconds;
        return { ...point, time: timeInSeconds };
      })
    );

    scheduleRegister([{
      name: scheduleName,
      vehicleId,
      turns: tableDataInSeconds,
    }]);

    setShowedLayout(0);
  };

  const validateNumberInput = (setter) => (value, min = 1, max) => {
    const newValue = Math.min(Math.max(min, Number(value)), max);
    setter(newValue);
  };

  // Utilidad para segundos totales
  const getTotalSeconds = (diff) => (diff?.minutes ?? 0) * 60 + (diff?.seconds ?? 0);

  const handleFillTable = (
    differences = customTimeDifferences,
    startTimeParam = startTime,
    cpStart = controlPointStart,
    cpEnd = controlPointEnd
  ) => {
    if (!cpStart || !cpEnd || !startTimeParam || !differences || !numLaps) {
      showAlert('Por favor, complete todos los campos para llenar la tabla.', 'warning');
      return;
    }

    setShowHeaders(true);

    const startIndex = routeOrder.indexOf(cpStart);
    const endIndex = routeOrder.indexOf(cpEnd);

    if (startIndex === -1 || endIndex === -1) {
      showAlert('Puntos de control inválidos.', 'warning');
      return;
    }

    const newTableData = [];
    let currentLapTime = dayjs(startTimeParam);

    for (let lap = 0; lap < numLaps; lap++) {
      const lapData = [];

      for (let index = 0; index < routeOrder.length; index++) {
        const label = routeOrder[index];
        const point = geofences.find((gf) => gf.label === label.replace(/\s(Ida|Vuelta)$/, ""));
        let time = "-";
        const diffObj = differences[index] || timeDifference;
        const secondsToAdd = getTotalSeconds(diffObj);

        if (numLaps === 1) {
          if (index >= startIndex && index <= endIndex) {
            time = currentLapTime.format("HH:mm:ss");
            currentLapTime = currentLapTime.add(secondsToAdd, "second");
          }
        } else if (lap === 0) {
          if (index >= startIndex) {
            time = currentLapTime.format("HH:mm:ss");
            currentLapTime = currentLapTime.add(secondsToAdd, "second");
          }
        } else if (lap > 0 && lap < numLaps - 1) {
          time = currentLapTime.format("HH:mm:ss");
          currentLapTime = currentLapTime.add(secondsToAdd, "second");
        } else if (lap === numLaps - 1) {
          if (index <= endIndex) {
            time = currentLapTime.format("HH:mm:ss");
            currentLapTime = currentLapTime.add(secondsToAdd, "second");
          }
        }

        lapData.push({
          id: point?.id || index,
          label,
          time,
        });
      }

      newTableData.push(lapData);
    }

    return newTableData;
  };

  const handleDifferenceChange = (index, newDifference) => {
    const updatedDifferences = [...customTimeDifferences];
    updatedDifferences[index] = newDifference;

    setCustomTimeDifferences(updatedDifferences);

    setTableData(handleFillTable(updatedDifferences));
  };

  const handleUpdateAllDifferences = (newDifference) => {
    if (!controlPointStart || !controlPointEnd || !startTime || !newDifference || typeof newDifference.minutes !== 'number' || typeof newDifference.seconds !== 'number' || !numLaps) {
      // console.log(newDifference);
      showAlert('Por favor, complete todos los campos para llenar la tabla.', 'warning');
      return;
    }

    setEnabledGenerateMultBtn(false);

    // Comparar si los valores son realmente nuevos
    const isNewDifference = !lastGeneratedTimeDifference || lastGeneratedTimeDifference.minutes !== newDifference.minutes || lastGeneratedTimeDifference.seconds !== newDifference.seconds;

    const updatedDifferences = isNewDifference ? routeOrder.map(() => ({ ...newDifference })) : [...customTimeDifferences];

    setCustomTimeDifferences(updatedDifferences);
    setTableData(handleFillTable(updatedDifferences));
    setLastGeneratedTimeDifference({ ...newDifference });
  };


  const saveMultiSchedules = (schedules) => {
    const multiSchedules = schedules.map(schedule => {
      const lapsData = handleFillTable(undefined, dayjs(schedule.startTime, "HH:mm:ss", true), schedule.cpStart, schedule.cpEnd).map((lap) =>
        lap.map((point) => {
          if (point.time === "-") {
            return { ...point, time: null };
          }

          const [hours, minutes, seconds] = point.time.split(":").map(Number);
          const timeInSeconds = hours * 3600 + minutes * 60 + seconds;
          return { ...point, time: timeInSeconds };
        })
      );

      return {
        name: schedule.name,
        vehicleId: schedule.vehicleId,
        turns: lapsData,
      };
    });
    // console.log(multiSchedules);
    scheduleRegister(multiSchedules);
    handleCloseModal();
  }

  const saveSchedulesImported = (schedules) => {
    const existingNames = new Set(userSchedules.map(schedule => schedule.name));

    const multischedules = schedules.map(schedule => {
      let scheduleName = schedule.name;

      // Asegurar un nombre único considerando userSchedules y nombres ya procesados
      while (existingNames.has(scheduleName)) {
        const baseName = "Horario";
        let counter = 1;

        // Encuentra el siguiente nombre único
        while (existingNames.has(`${baseName} ${counter}`)) {
          counter++;
        }
        scheduleName = `${baseName} ${counter}`;
      }

      // Añade el nombre único al conjunto para evitar duplicados
      existingNames.add(scheduleName);

      const updatedTurns = schedule.turns.map(turn =>
        turn.map(point => {
          const matchingRoute = userRoute.find(route => route.label.replace(' Ida', '').replace(' Vuelta', '').toLowerCase() === point.label.toLowerCase());
          return matchingRoute ? { ...point, label: matchingRoute.label, id: matchingRoute.id } : point;
        })
      );

      // Devuelve el horario procesado
      return { name: scheduleName, vehicleId: null, turns: updatedTurns };
    });


    // Registra los horarios actualizados
    // scheduleRegister(multischedules);

    setPrevSchedules(multischedules);
    setIsViewModalOpen(true);

    // console.log(multischedules);

    setEnabledGenerateMultBtn(true);
    setMultiScheduleCount(2);

    setTimeout(() => {
      handleOpenImportModal();
    }, 100);

  };

  const handleScheduleNameChange = (e) => {
    const name = e.target.value;
    setScheduleName(name);

    // Validar si el nombre del horario ya existe
    const isDuplicate = userSchedules.some(schedule => schedule.name === name);

    if (isDuplicate) {
      setScheduleNameError(true);
      setErrorMessage("Horario duplicado");
    } else {
      setScheduleNameError(false);
      setErrorMessage("");
    }
  };

  const handleVehicleChange = (newVehicleId) => {
    const isAssigned = userSchedules.some(
      (schedule) => schedule.vehicleId === parseInt(newVehicleId)
    );

    if (isAssigned) {
      // Guardar cambios pendientes y mostrar el modal
      setPendingVehicleChange({ newVehicleId });
      setIsConfirmModalOpen(true);
    } else {
      setVehicle(newVehicleId);
    }
  };

  return (
    <div className='card_content new-schedule-layout' style={{ display: 'flex', flexDirection: 'column' }}>
      <Button onClick={() => setShowedLayout(0)} style={{ alignSelf: 'flex-start', fontSize: '0.7rem', alignItems: 'flex-start' }}>
        <span className="material-symbols-rounded icon" style={{ fontSize: '18px' }}>arrow_back</span>
        Volver
      </Button>
      <h2>Crear Nuevo Horario</h2>
      <div style={{ overflowY: 'hidden', display: 'flex', flexDirection: 'column', gap: '1rem', flex: 1, paddingBottom: '1rem', paddingTop: '0.5rem' }}>
        <div className="form_container">
          <div className="fields_container">
            <TextField
              fullWidth
              label="Nombre del horario"
              variant="outlined"
              value={scheduleName}
              onChange={handleScheduleNameChange}
              error={scheduleNameError} // Muestra el borde rojo si hay un error
              helperText={scheduleNameError ? errorMessage : ""} // Muestra el mensaje de error
              sx={{
                '& .MuiInputLabel-root': {
                  color: 'var(--color-dark)', // Cambia el color de la label
                  fontSize: '0.8rem',
                },
                '& .MuiInputLabel-shrink': {
                  fontSize: '1rem', // Tamaño de la label cuando está en foco
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                  },
                  '&:not(.Mui-focused):hover fieldset': {
                    borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                  },
                },
                '& .MuiInputBase-input': {
                  color: 'var(--color-dark)',
                  fontSize: '0.8rem', // Tamaño del texto de entrada
                  padding: '1rem 12px'
                },
              }}
            />
            <FormControl fullWidth >
              <InputLabel id="vehicle-select-label" sx={{ color: 'var(--color-dark)' }}>Asignar vehículo</InputLabel>
              <Select
                labelId="vehicle-select-label"
                value={vehicle}
                onChange={(e) => handleVehicleChange(e.target.value)}
                label="Asignar vehículo"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                  },
                  '&:not(.Mui-focused):hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                  },
                  '& .MuiInputBase-input': {
                    color: 'var(--color-dark)',
                    fontSize: '0.8rem', // Tamaño del texto de entrada
                    padding: '0.9rem 12px'
                  },
                }}
              >
                <MenuItem value="Ninguno" style={{ fontSize: '0.8rem' }}>Ninguno</MenuItem>
                {devices
                  .filter((device) => device.label).sort(mixedNaturalSort)
                  .map((device, index) => (
                    <MenuItem key={index} value={device.id} style={{ fontSize: '0.8rem' }}>
                      {device.label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimeField
                label="Hora de Inicio"
                value={startTime}
                onChange={(newValue) => setStartTime(newValue)}
                format="HH:mm:ss"
                fullWidth
                sx={{
                  '& .MuiInputLabel-root': {
                    color: 'var(--color-dark)', // Cambia el color de la label
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                    },
                    '&:not(.Mui-focused):hover fieldset': {
                      borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.8rem', // Tamaño del texto dentro del campo de entrada
                    padding: '1rem 12px', // Ajuste de padding para alinear correctamente el texto
                    color: 'var(--color-dark)', // Color del texto
                  }
                }}
              />
            </LocalizationProvider>
            <FormControl fullWidth >
              <InputLabel id="control-point-start-label" sx={{
                color: 'var(--color-dark)',
                fontSize: '0.8rem', // Tamaño de la etiqueta cuando NO está en foco
                '&.MuiInputLabel-shrink': {
                  fontSize: '1rem', // Mantiene el mismo tamaño cuando está en foco
                }
              }}>Punto de Control Inicial</InputLabel>
              <Select
                labelId="control-point-start-label"
                value={controlPointStart}
                onChange={(e) => setControlPointStart(e.target.value)}
                label="Punto de Control Inicial"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                  },
                  '&:not(.Mui-focused):hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                  },
                  '& .MuiSelect-select': {
                    color: 'var(--color-dark)',
                    fontSize: '0.8rem', // Tamaño del texto dentro del `Select`
                    padding: '0.85rem 12px'
                  }
                }}
              >
                {routeOrder.map((geofence, index) => (
                  <MenuItem key={index} value={geofence} style={{ fontSize: '0.8rem' }}>
                    {geofence}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth >
              <InputLabel id="control-point-end-label" sx={{
                color: 'var(--color-dark)',
                fontSize: '0.8rem', // Tamaño de la etiqueta cuando NO está en foco
                '&.MuiInputLabel-shrink': {
                  fontSize: '1rem', // Mantiene el mismo tamaño cuando está en foco
                }
              }}>Punto de Control Final</InputLabel>
              <Select
                labelId="control-point-end-label"
                value={controlPointEnd}
                onChange={(e) => setControlPointEnd(e.target.value)}
                label="Punto de Control Final"
                sx={{
                  color: 'var(--color-dark)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                  },
                  '&:not(.Mui-focused):hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                  },
                  '& .MuiSelect-select': {
                    color: 'var(--color-dark)',
                    fontSize: '0.8rem', // Tamaño del texto dentro del `Select`
                    padding: '0.85rem 12px'
                  }
                }}
              >
                {routeOrder.map((geofence, index) => (
                  <MenuItem key={index} value={geofence} style={{ fontSize: '0.8rem' }}>
                    {geofence}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimeField
                fullWidth
                label="Diferencia entre puntos (mm:ss)"
                variant="outlined"
                format="mm:ss"
                value={dayjs().hour(0).minute(timeDifference?.minutes ?? 5).second(timeDifference?.seconds ?? 0)}
                onChange={(newValue) => {
                  if (!newValue) return;
                  let minutes = newValue.minute();
                  let seconds = newValue.second();
                  let totalSeconds = minutes * 60 + seconds;
                  // Limita a 30 minutos máximo (30:00 == 1800 segundos)
                  if (totalSeconds < 60) {
                    minutes = 1;
                    seconds = 0;
                    totalSeconds = 60;
                  } else if (totalSeconds > 1800) {
                    minutes = 30;
                    seconds = 0;
                    totalSeconds = 1800;
                  }
                  setTimeDifference({ minutes, seconds });
                }}

                sx={{
                  '& .MuiInputLabel-root': {
                    color: 'var(--color-dark)', // Cambia el color de la label
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                    },
                    '&:not(.Mui-focused):hover fieldset': {
                      borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.8rem', // Tamaño del texto dentro del campo de entrada
                    padding: '1rem 12px', // Ajuste de padding para alinear correctamente el texto
                    color: 'var(--color-dark)', // Color del texto
                  }
                }}
              />
            </LocalizationProvider>
            <TextField
              fullWidth
              type="number"
              label="Número de Vueltas"
              variant="outlined"
              value={numLaps}
              onChange={(e) => validateNumberInput(setNumLaps)(e.target.value, 1, 30)}
              slotProps={{
                input: {
                  min: 1,
                  max: 30,
                  step: 1,
                  onClick: (event) => event.target.select(),
                },
              }}
              sx={{
                '& .MuiInputLabel-root': {
                  color: 'var(--color-dark)', // Cambia el color de la label
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                  },
                  '&:not(.Mui-focused):hover fieldset': {
                    borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                  },
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.8rem', // Tamaño del texto dentro del campo de entrada
                  padding: '1rem 12px', // Ajuste de padding para alinear correctamente el texto
                  color: 'var(--color-dark)', // Color del texto
                }
              }}
            />
          </div>
          <div className="buttons_container">
            <Button style={{ fontSize: '0.7rem' }} onClick={() => setOpenRouteModal(true)}>
              Modificar Ruta
            </Button>
            <RouteModal onOpen={openRouteModal} onClose={setOpenRouteModal} userRoute={userRoute} userUpdate={userUpdate} />
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleUpdateAllDifferences(timeDifference)}
              className="generate_schedule_btn"
              style={{ fontSize: '0.7rem' }}
            >
              Generar Horario
            </Button>
          </div>
        </div>
        <div className="table_wrapper">
          <table>
            <thead>
              <tr>
                {routeOrder.map((label, index) => (
                  <th key={index}>{label}</th>
                ))}
              </tr>
              {showHeaders && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <tr>
                    {customTimeDifferences.map((difference, index) => (
                      <th key={index}>
                        <TimeField
                          value={dayjs().hour(0).minute(difference?.minutes ?? 0).second(difference?.seconds ?? 0)}
                          onChange={(newValue) => {
                            if (!newValue) return; // Previene errores si se borra
                            let minutes = newValue.minute();
                            let seconds = newValue.second();
                            let totalSeconds = minutes * 60 + seconds;
                            // Limita a 30 minutos máximo (30:00 == 1800 segundos)
                            if (totalSeconds < 60) {
                              minutes = 1;
                              seconds = 0;
                              totalSeconds = 60;
                            } else if (totalSeconds > 1800) {
                              minutes = 30;
                              seconds = 0;
                              totalSeconds = 1800;
                            }
                            handleDifferenceChange(index, { minutes, seconds });
                          }}
                          format="mm:ss"
                          fullWidth
                          variant="standard"
                          sx={{

                            backgroundColor: 'white',
                            '& .MuiInputLabel-root': {
                              color: 'var(--color-dark)',
                            },
                            '& .MuiOutlinedInput-root': {
                              padding: '0',
                              '& fieldset': {
                                borderColor: 'var(--color-dark-variant)',
                              },
                              '&:not(.Mui-focused):hover fieldset': {
                                borderColor: 'var(--color-dark)',
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '0.7rem',
                              padding: '0.5rem 0',
                              color: 'var(--color-dark)',
                              textAlign: 'center'
                            }
                          }}
                        />

                      </th>
                    ))}
                  </tr>
                </LocalizationProvider>
              )}
            </thead>
            <tbody>
              {tableData && tableData.map((lap, lapIndex) => (
                <tr key={lapIndex}>
                  {lap.map((point, pointIndex) => (
                    <td key={pointIndex}>
                      {point.time !== "-" ? point.time : ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="button_container">
        <Button onClick={handleOpenImportModal}>
          Importar de Excel
        </Button>
        {enabledGenerateMultBtn ? (
          <BootstrapTooltip title="Genere un horario para utilizarlo de referencia">
            <span>
              <Button
                onClick={handleOpenMultiScheduleModal}
                disabled={true}
              >
                Generar varios horarios
              </Button>
            </span>
          </BootstrapTooltip>
        ) : (
          <Button
            onClick={handleOpenMultiScheduleModal}
            disabled={false}
          >
            Generar varios horarios
          </Button>
        )}
        <Button className="cancel_button" onClick={handleCloseModal}>
          Cancelar
        </Button>
        <Button className="accept_button" onClick={handleAccept}>
          Aceptar
        </Button>
      </div>

      <ConfirmModal title={'Confirmación'} text={`Este vehículo ya se encuentra asignado al horario "${userSchedules.find((schedule) => schedule.vehicleId === pendingVehicleChange?.newVehicleId)?.name || ''}". ¿Desea asignarlo a este horario de cualquier manera?`} isOpen={isConfirmModalOpen} onClose={setIsConfirmModalOpen}
        confirmFunction={() => {
          if (pendingVehicleChange) {
            setVehicle(pendingVehicleChange.newVehicleId);
          }
          setIsConfirmModalOpen(false);
        }} />
      <ViewSchedulesModal open={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} schedules={prevSchedules} prev={true} scheduleRegister={(schedules) => {setShowedLayout(0); scheduleRegister(schedules)}} />
      <ImportExcelModal open={openImportModal} onClose={handleOpenImportModal} onSave={saveSchedulesImported} userSchedules={userSchedules} showAlert={showAlert} userRoute={userRoute} geofences={geofences} />
      <MultiSchedulesModal
        onOpen={multiScheduleModalOpen}
        onClose={handleCloseMultiScheduleModal}
        onSave={saveMultiSchedules}
        devices={devices}
        userSchedules={userSchedules}
        routeOrder={routeOrder}
        multiScheduleCount={multiScheduleCount}
        setMultiScheduleCount={setMultiScheduleCount}
        scheduleName={scheduleName}
        setScheduleName={setScheduleName}
        controlPointStart={controlPointStart}
        controlPointEnd={controlPointEnd}
        startTime={startTime}
        showAlert={showAlert}
      />
    </div>
  );
};

export default NewSchedule;
